import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillPanel } from '../../../types';
import { DrillCruxStat } from './DrillCruxStat';
import { DrillTile } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';

export function DrillBrainSection({
  panel,
}: {
  metricKey: string;
  panel: Extract<DrillPanel, { kind: 'brainSeating' }>;
}) {
  const coversShare = panel.covers
    ? Math.round((panel.autoCovers / panel.covers) * 100)
    : 0;
  const showShift = panel.byShift != null && panel.byShift.length > 0;
  const showOrigin = panel.byOrigin.length > 0;

  const inspector = useMemo(
    () => (
      <>
        <div className="dcrux-stat-row" style={{ marginBottom: 12 }}>
          <DrillCruxStat
            value={`${panel.coveragePct}%`}
            label={i18n.t('performance:drillUi.flagCoverage')}
          />
          <DrillCruxStat
            value={fmt.num(panel.seatable)}
            label={i18n.t('performance:drillUi.seatableReservations')}
          />
        </div>
        <div className="dcrux-brain-cols">
          {showShift && (
            <div>
              <div className="dcrux-col-lab">{i18n.t('performance:drillUi.byShift')}</div>
              <div className="dcrux-si-list">
                {panel.byShift!.map(s => {
                  const max = Math.max(
                    ...panel.byShift!.map(x => x.autoPct),
                    0.0001,
                  );
                  return (
                    <div className="dcrux-si-row" key={s.shiftId}>
                      <span className="dcrux-si-name">{s.label}</span>
                      <span className="dcrux-si-track">
                        <span
                          className="dcrux-si-fill"
                          style={{ width: `${(s.autoPct / max) * 100}%` }}
                        />
                      </span>
                      <span className="dcrux-si-val">{s.autoPct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {showOrigin && (
            <div>
              <div className="dcrux-col-lab">{i18n.t('performance:drillUi.byOrigin')}</div>
              <div className="dcrux-si-list">
                {panel.byOrigin.map(o => {
                  const max = Math.max(
                    ...panel.byOrigin.map(x => x.autoSeated),
                    0.0001,
                  );
                  return (
                    <div className="dcrux-si-row" key={o.origin}>
                      <span className="dcrux-si-name">{o.label}</span>
                      <span className="dcrux-si-track">
                        <span
                          className="dcrux-si-fill"
                          style={{
                            width: `${(o.autoSeated / max) * 100}%`,
                          }}
                        />
                      </span>
                      <span className="dcrux-si-val">
                        {fmt.num(o.autoSeated)} · {o.autoPct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </>
    ),
    [panel, showShift, showOrigin],
  );

  const originBars = panel.byOrigin.slice(0, 5).map(o => ({
    name: o.label,
    value: o.autoPct,
  }));

  return (
    <DrillTile
      sectionId="brain"
      span={4}
      title={panel.title}
      meta={i18n.t('performance:drillUi.prov.measured')}
      inspector={inspector}
      n={i18n.t('performance:drillUi.nKnownSeating', {
        seatable: fmt.num(panel.seatable),
        booked: fmt.num(panel.booked),
      })}
    >
      <div className="ddrill-stat-inline">
        <strong style={{ color: 'var(--accent)' }}>{panel.autoPct}%</strong>
        <span>{i18n.t('performance:drillUi.autoGuests', { pct: coversShare })}</span>
      </div>
      {originBars.length ? (
        <DrillBarChart
          data={originBars}
          fmtVal={v => `${v}%`}
          height={120}
        />
      ) : null}
    </DrillTile>
  );
}
